/* eslint-disable react/prop-types, react/destructuring-assignment */
import clsx from 'clsx'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Col, Form, Modal, Row } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import PerfectScrollbar from 'react-perfect-scrollbar'
import Select, { components, createFilter } from 'react-select'
import { multiSelectStyle } from '../../../../../../_augmentt/utils/styles'
import ScrollMenu from '../../../../../partials/content/ScrollMenu'
import { actions as M365_ACTIONS } from '../../integrations/m365/actions'
import { groupTypes } from '../../integrations/m365/groupTypes'
import { cloneChildComponents, Option } from '../../utils/helpers'

export const filterOption = (option, inputValue) =>
  (option.label.toString().toLowerCase().match(inputValue.toLowerCase()) || []).length > 0

// Table / Engage row payloads often expose M365 groups only on `groupsByType.M365Group` (see
// `useFreshEmployeeData`). Using only `m365Groups` leaves "Remove from" empty in Manage M365 groups.
function getEmployeeM365Groups(employee) {
  if (!employee) {
    return []
  }
  const fromFlat = employee.m365Groups
  if (Array.isArray(fromFlat) && fromFlat.length > 0) {
    return fromFlat
  }
  return employee.groupsByType?.M365Group || []
}

function getGroupEntryId(group) {
  if (group == null) {
    return undefined
  }
  return group.id ?? group.groupId ?? group.GroupId
}

function getGroupEntryName(group) {
  if (group == null) {
    return ''
  }
  return group.name ?? group.displayName ?? group.Name ?? ''
}

function getGroupsForEmployeeByAction(employee, action) {
  if (action === M365_ACTIONS.M365_MANAGE_GROUPS) {
    return getEmployeeM365Groups(employee)
  }
  return employee?.groups ?? []
}

function ManageGroups(props) {
  const { children, ...rest } = props
  const { intl, setDisableSubmit, actionData, setActionData, setCompletedActions, action, allEmployeesGroups } = rest

  const selectedEmployees = useMemo(
    () => (rest?.selectedEmployees?.length ? rest?.selectedEmployees : [rest?.selectedEmployee]),
    [rest?.selectedEmployees, rest?.selectedEmployee],
  )

  const [selectedGroupsToAdd, setSelectedGroupsToAdd] = useState([])
  const [currentGroupsInCommon, setCurrentGroupsInCommon] = useState([])
  const [selectedGroupsToRemove, setSelectedGroupsToRemove] = useState([])
  const [showGroupsMenu, setShowGroupsMenu] = useState(false)
  const [groupOptions, setGroupOptions] = useState([])

  const groupsRef = useRef() // References the group selector. Used to close the menu using handleClickOutside

  // Track current employee groups for change detection
  const currentEmployeeGroups = useMemo(
    () =>
      selectedEmployees
        .map((emp) => {
          const groups = getGroupsForEmployeeByAction(emp, action)
          return groups
            .map((g) => getGroupEntryId(g))
            .filter((id) => id != null)
            .sort()
            .join(',')
        })
        .join('|'),
    [selectedEmployees, action],
  )

  // Reset selections when groups change
  useEffect(() => {
    setSelectedGroupsToAdd([])
    setSelectedGroupsToRemove([])
    setShowGroupsMenu(false)
  }, [currentEmployeeGroups])

  useEffect(() => {
    // eslint-disable-next-line no-use-before-define
    formatGroupOptions()
    // eslint-disable-next-line no-use-before-define
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      // eslint-disable-next-line no-use-before-define
      document.removeEventListener('click', handleClickOutside, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEmployeesGroups])

  // Handles setting up actionData for the taskParameters as well as for success body data. Also sets the completed actions
  useEffect(() => {
    const newActionData = {}

    // Helper
    const formatArrayFromSource = (sourceArray, key) =>
      sourceArray.map((sourceObject) => (key === 'label' ? { name: sourceObject[key] } : sourceObject[key]))

    // Set the task parameters
    newActionData.groupIdsToAdd = formatArrayFromSource(selectedGroupsToAdd, 'id')
    newActionData.groupIdsToRemove = formatArrayFromSource(selectedGroupsToRemove, 'id')
    newActionData.employeeEmails = formatArrayFromSource(selectedEmployees, 'email')
    newActionData.employeeIdentifiers = formatArrayFromSource(selectedEmployees, 'employeeIdentifier')

    // Set the success body data, reference the config getSuccessData
    newActionData.groupsAdded = formatArrayFromSource(selectedGroupsToAdd, 'label')
    newActionData.groupsRemoved = formatArrayFromSource(selectedGroupsToRemove, 'label')

    // Set the selected employee names to display in the success body as well
    newActionData.employeesUpdated = selectedEmployees.map((employee) => ({
      employeeFullName: employee.displayName || `${employee.firstName} ${employee.lastName}`,
    }))

    setActionData({ ...actionData, ...newActionData })

    // Set the completed actions to display in the success body
    if (selectedGroupsToAdd.length) {
      const completedAction =
        action === M365_ACTIONS.M365_MANAGE_GROUPS ? 'ENGAGE.ADDED_TO_M365_GROUP' : 'ENGAGE.ADDED_TO_GROUP'
      setCompletedActions((alreadyCompletedActions) => [...alreadyCompletedActions, completedAction])
    }
    if (selectedGroupsToRemove.length) {
      const completedAction =
        action === M365_ACTIONS.M365_MANAGE_GROUPS ? 'ENGAGE.REMOVED_FROM_365_GROUP' : 'ENGAGE.REMOVED_FROM_GROUP'
      setCompletedActions((alreadyCompletedActions) => [...alreadyCompletedActions, completedAction])
    }

    // Deciee if we should disable or enable the submit button
    if (!selectedGroupsToAdd?.length && !selectedGroupsToRemove?.length) {
      setDisableSubmit(true)
    } else {
      setDisableSubmit(false)
    }
    // eslint-disable-next-line no-use-before-define
    formatGroupOptions()
  }, [selectedGroupsToAdd, selectedGroupsToRemove, currentGroupsInCommon]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handles setting up the groups that can be removed for all selected customers
  // Recalculate whenever employee data changes
  useEffect(() => {
    // Create a hash of group ids with the number of occurances. This way we can easily tell which groups are common among all selected employees
    const groupsInCommonMap = new Map()
    const groupIdToName = new Map()
    // eslint-disable-next-line no-restricted-syntax
    for (const employee of selectedEmployees) {
      const groups = getGroupsForEmployeeByAction(employee, action)

      // eslint-disable-next-line no-restricted-syntax
      for (const group of groups) {
        const gid = getGroupEntryId(group)
        if (gid == null) {
          // eslint-disable-next-line no-continue
          continue
        }
        const gname = getGroupEntryName(group)
        if (!groupsInCommonMap.get(gid)) {
          groupsInCommonMap.set(gid, 1)
          groupIdToName.set(gid, gname)
        } else {
          groupsInCommonMap.set(gid, groupsInCommonMap.get(gid) + 1)
        }
      }
    }

    const commonGroups = []
    groupsInCommonMap.forEach((value, key) => {
      if (value === selectedEmployees?.length) {
        commonGroups.push({ id: key, name: groupIdToName.get(key) })
      }
    })

    setCurrentGroupsInCommon(commonGroups)
  }, [selectedEmployees, action]) // Recalculate when employee data changes

  // Helper to get id
  const helperGetId = (group) => group.id

  // Format options in group selector
  const formatGroupOptions = () => {
    const options = []

    // Format customer groups
    // eslint-disable-next-line no-restricted-syntax
    const allGroups = allEmployeesGroups.filter((x) => x.groupType === groupTypes.M365Group)

    // eslint-disable-next-line no-restricted-syntax
    for (const group of allGroups) {
      const { id, name } = group
      // If we are removing, remove the option to add the group
      if (selectedGroupsToRemove.map(helperGetId).includes(id)) {
        // Don't mutate state here - just skip adding this group to options
        continue // eslint-disable-line no-continue
      }

      // If the group is already assigned to all selected employees, do not not display it in the "Add to Group" options
      if (currentGroupsInCommon.filter((groupInCommon) => groupInCommon.id === id).length) {
        continue // eslint-disable-line no-continue
      }

      options.push({
        value: id,
        label: name,
      })
    }

    // Sort by name
    options.sort((a, b) => a.label.localeCompare(b.label))
    setGroupOptions(options)
  }

  // Handles selecting/unselecting a group from the remove groups section
  const toggleGroupAddOrRemove = (groupObj) => {
    const { id: groupId } = groupObj

    // Add the group to be removed or remove it if already there
    const indexOfGroup = selectedGroupsToRemove.map(helperGetId).indexOf(groupId)
    if (indexOfGroup > -1) {
      // Remove the group it from the list
      const newGroupsList = [...selectedGroupsToRemove]
      newGroupsList.splice(indexOfGroup, 1) // remove 1 record
      setSelectedGroupsToRemove(newGroupsList)
    } else {
      // Add the new group to the groups to remove
      setSelectedGroupsToRemove([...selectedGroupsToRemove, groupObj])
    }
  }

  // Handles clicking on the groups to add selector
  const handleAddToGroup = (target) => {
    if (target === 'groups') {
      if (showGroupsMenu) {
        setShowGroupsMenu(false)
        if (groupsRef.current.select.inputRef) {
          groupsRef.current.select.inputRef.blur()
        }
      } else {
        setShowGroupsMenu(true)
      }
    }
  }

  // Outside click handler for hiding group multi-select menu (workaround due to closeMenuOnSelect being disabled)
  const handleClickOutside = (event) => {
    if (
      groupsRef.current &&
      !groupsRef.current.select.controlRef.contains(event.target) &&
      !groupsRef.current.select?.focusedOptionRef?.contains(event.target)
    ) {
      setShowGroupsMenu(false)
      if (groupsRef.current.select.inputRef) {
        groupsRef.current.select.inputRef.blur()
      }
    }
  }

  // Component override for react-select
  // eslint-disable-next-line react/no-unstable-nested-components, no-shadow
  function ValueContainer({ children, getValue, ...props }) {
    const { length } = getValue()
    return (
      <components.ValueContainer {...props}>
        {length === 1 && (
          <>
            <span className="multi-select-value">{props.selectProps.value[0].label}</span>
            <ClearIndicator {...props} />
          </>
        )}
        {length > 1 && (
          <>
            <span className="multi-select-value">{length}</span>
            <ClearIndicator {...props} />
            <components.Placeholder {...props} className="multi-select-label">
              {props.selectProps.placeholder}
            </components.Placeholder>
          </>
        )}
      </components.ValueContainer>
    )
  }

  // Clear button component override
  // eslint-disable-next-line react/no-unstable-nested-components, no-shadow
  function ClearIndicator({ innerRef, children, ...props }) {
    const onClear = (e) => {
      if (props.selectProps?.name === 'groups') {
        setSelectedGroupsToAdd([])
      }

      e.stopPropagation()
      e.preventDefault()
    }

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div ref={innerRef} className="multi-select-clear" onClick={onClear}>
        <components.ClearIndicator {...props}>
          <span className="icon-xs flaticon2-cross p-2" />
        </components.ClearIndicator>
      </div>
    )
  }

  return (
    <PerfectScrollbar options={{ wheelSpeed: 2, wheelPropagation: false }} className="paginator-margin-bottom">
      <Form>
        <Modal.Body className="sidebar-body">
          <Row>
            <Col sm="12" md="12" lg="12">
              {currentGroupsInCommon?.length > 0 && (
                <p className="font-bolder mb-3 manageGroup">
                  <FormattedMessage
                    id={
                      action === M365_ACTIONS.M365_MANAGE_GROUPS
                        ? 'ENGAGE.REMOVE_FROM_M365_GROUPS'
                        : 'ENGAGE.REMOVE_FROM_GROUPS'
                    }
                  />
                </p>
              )}

              <p className="ms-3">
                {currentGroupsInCommon.map((group, index) => (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                  <span
                    key={index} // eslint-disable-line react/no-array-index-key
                    value={group.id}
                    onClick={() =>
                      toggleGroupAddOrRemove({
                        id: group.id,
                        label: group.name,
                      })
                    }
                    className={`chip-select ${clsx({
                      'chip-danger': selectedGroupsToRemove.map(helperGetId).includes(group.id),
                    })}`}
                  >
                    {group.name}
                  </span>
                ))}
              </p>

              {selectedGroupsToRemove?.length > 0 && (
                <p className="chip-danger">
                  <FormattedMessage id="ENGAGE.REMOVE_GROUP_MESSAGE" />
                </p>
              )}

              {groupOptions?.length > 0 && (
                <>
                  <p className="font-bolder mb-3 margin-t-40 manageGroup">
                    <FormattedMessage
                      id={
                        action === M365_ACTIONS.M365_MANAGE_GROUPS
                          ? 'ENGAGE.ADD_TO_M365_GROUPS'
                          : 'ENGAGE.ADD_TO_GROUPS'
                      }
                    />
                  </p>
                  <div className="ps-3">
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <div className="select-container" onClick={() => handleAddToGroup('groups')}>
                      <Select
                        isMulti
                        id="groupsSelect"
                        name="groups"
                        ref={groupsRef}
                        menuIsOpen={showGroupsMenu}
                        autoBlur
                        isSearchable
                        className="inline"
                        classNamePrefix="multi-select"
                        styles={multiSelectStyle}
                        options={groupOptions}
                        value={selectedGroupsToAdd}
                        closeMenuOnSelect={false}
                        onChange={(groups) => {
                          if (groups && groups.length) {
                            // convert groups to id, label format
                            const formattedGroupArr = groups.map((g) => ({
                              id: g.value,
                              label: g.label,
                              ...g,
                            }))
                            // For task
                            setSelectedGroupsToAdd(formattedGroupArr)
                          } else {
                            // Nothing selected
                            setSelectedGroupsToAdd([])
                          }
                        }}
                        hideSelectedOptions={false}
                        isClearable={false}
                        placeholder={
                          selectedGroupsToAdd?.length
                            ? intl.formatMessage({ id: 'ENGAGE.GROUPS' })
                            : intl.formatMessage({
                                id: 'ENGAGE.SELECT_GROUPS',
                              })
                        }
                        components={
                          selectedGroupsToAdd?.length
                            ? {
                                MenuList: ScrollMenu,
                                ClearIndicator,
                                Option,
                                ValueContainer,
                              }
                            : { MenuList: ScrollMenu, Option }
                        }
                        filterOption={createFilter({
                          matchFrom: 'any',
                          stringify: (option) => `${option.label}`,
                        })}
                      />
                    </div>
                  </div>
                </>
              )}
            </Col>
          </Row>
        </Modal.Body>

        {/* Footer Child */}
        {cloneChildComponents({ ...rest }, children)}
      </Form>
    </PerfectScrollbar>
  )
}

export default ManageGroups
